import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

export default function AdminAIGenerator() {
  const [cats, setCats] = useState([]);
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", category_id: "", target_audience: "General learners",
    country_context: "Canada", language: "English", level: "Beginner",
    num_modules: 3, duration_hours: 6, price_usd: 49,
    certificate_title: "Certificate of Completion",
    final_test_required: true, quiz_questions_per_module: 4,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/api/categories").then(({data}) => {
      setCats(data);
      if (data.length && !form.category_id) setForm((f) => ({...f, category_id: data[0].id}));
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title required");
    if (!form.category_id) return toast.error("Pick a category");
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/ai/generate-course", form);
      toast.success("Course generated as draft. Review and publish.");
      nav(`/admin/courses/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-ai-generator">
      <div className="label-eyebrow">AI tools</div>
      <h1 className="font-display font-black text-3xl mt-1">AI Course Generator</h1>
      <p className="text-sm text-[#4B5563] mt-1 max-w-2xl">Generate a full course outline, lessons, quizzes and final test from a prompt. Saved as draft — review and edit before publishing.</p>

      <form onSubmit={submit} className="mt-6 bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold">Course title</label>
            <input data-testid="gen-title" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
              placeholder="e.g. Driving Tests for Newcomers — Ontario"
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Category</label>
            <select data-testid="gen-category" value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Target audience</label>
            <input data-testid="gen-audience" value={form.target_audience} onChange={(e) => setForm({...form, target_audience: e.target.value})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Country/context</label>
            <input value={form.country_context} onChange={(e) => setForm({...form, country_context: e.target.value})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Language</label>
            <select value={form.language} onChange={(e) => setForm({...form, language: e.target.value})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
              <option>English</option><option>French</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Level</label>
            <select value={form.level} onChange={(e) => setForm({...form, level: e.target.value})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
              <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Number of modules</label>
            <input type="number" min={2} max={6} value={form.num_modules} onChange={(e) => setForm({...form, num_modules: parseInt(e.target.value)})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Duration (hours)</label>
            <input type="number" min={1} step={0.5} value={form.duration_hours} onChange={(e) => setForm({...form, duration_hours: parseFloat(e.target.value)})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Price (USD)</label>
            <input data-testid="gen-price" type="number" min={0} value={form.price_usd} onChange={(e) => setForm({...form, price_usd: parseFloat(e.target.value)})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Certificate title</label>
            <input value={form.certificate_title} onChange={(e) => setForm({...form, certificate_title: e.target.value})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Quiz questions per module</label>
            <input type="number" min={1} max={10} value={form.quiz_questions_per_module} onChange={(e) => setForm({...form, quiz_questions_per_module: parseInt(e.target.value)})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input type="checkbox" id="final" checked={form.final_test_required} onChange={(e) => setForm({...form, final_test_required: e.target.checked})} />
            <label htmlFor="final" className="text-sm font-semibold">Include final test</label>
          </div>
        </div>

        <button data-testid="gen-submit" type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating (30–60s)…</> : <><Sparkles className="w-4 h-4" /> Generate course</>}
        </button>
        <p className="text-xs text-[#4B5563]">Generation can take up to 60 seconds. The course is saved as a draft for you to review.</p>
      </form>
    </div>
  );
}
